import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import FlatChart from './FlatChart';
import SiteVisits from './SiteVisits';

const SECTION_STYLE = {
  background: '#fff', borderRadius: '12px', padding: '24px',
  marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  border: '1px solid #e5e7eb'
};
const SECTION_TITLE = {
  fontSize: '16px', fontWeight: '700', color: '#1e293b',
  marginBottom: '16px', paddingBottom: '10px',
  borderBottom: '2px solid #e2e8f0', display: 'flex',
  alignItems: 'center', gap: '8px'
};
const GRID2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' };
const GRID3 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' };
const LABEL = {
  fontSize: '12px', fontWeight: '600', color: '#64748b',
  textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px'
};
const INPUT = {
  width: '100%', padding: '10px 12px', borderRadius: '8px',
  border: '1px solid #d1d5db', fontSize: '14px', color: '#1e293b',
  outline: 'none', transition: 'border 0.2s',
  boxSizing: 'border-box'
};
const SELECT_STYLE = { ...INPUT, background: '#fff' };
const BADGE = (color) => ({
  display: 'inline-block', padding: '4px 10px', borderRadius: '20px',
  fontSize: '12px', fontWeight: '600',
  background: color === 'green' ? '#d1fae5' : color === 'amber' ? '#fef3c7' : color === 'blue' ? '#dbeafe' : '#f1f5f9',
  color: color === 'green' ? '#065f46' : color === 'amber' ? '#92400e' : color === 'blue' ? '#1e40af' : '#475569'
});
const STAT_CARD = {
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  borderRadius: '12px', padding: '20px', color: '#fff', textAlign: 'center'
};

const formatCurrency = (val) => {
  if (!val && val !== 0) return '-';
  const num = parseFloat(val);
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
  if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
  return `₹${num.toLocaleString('en-IN')}`;
};

const MasterData = ({ apiBase }) => {
  const [masterData, setMasterData] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');

  const fetchMasterData = useCallback(async () => {
    try {
      setLoading(true);
      // First get projects for this tenant if not loaded
      if (!window._trcProjectId) {
        const projRes = await axios.get(`${apiBase}/projects`);
        const projList = projRes.data || [];
        if (projList.length === 0) {
          setLoading(false);
          return;
        }
        window._trcProjectId = projList[0].id;
      }
      const res = await axios.get(`${apiBase}/projects/${window._trcProjectId}/master`);
      setMasterData(res.data);
      setFormData(res.data.project || {});
    } catch (err) {
      console.error('Error fetching master data:', err);
      alert('Error loading master data: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => { fetchMasterData(); }, [fetchMasterData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSaveStatus(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${apiBase}/projects/${window._trcProjectId || 1}/master`, formData);
      setSaveStatus('success');
      await fetchMasterData();
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err) {
      console.error('Error saving:', err);
      setSaveStatus('error');
      alert('Error saving: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '32px', marginBottom: '12px' }}>🏛️</div>
        <div style={{ color: '#64748b' }}>Loading Master Data...</div>
      </div>
    </div>
  );

  if (!masterData) return <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>Failed to load master data</div>;

  const { towers, typologies, costSheetItems, inventorySummary, statusSummary } = masterData;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'chart', label: 'Flat chart', icon: '🧩' },
    { id: 'sitevisits', label: 'Site visits', icon: '📍' },
    { id: 'project', label: 'Project Info', icon: '🏗️' },
    { id: 'rera', label: 'RERA & Legal', icon: '📋' },
    { id: 'pricing', label: 'Pricing', icon: '💰' },
    { id: 'charges', label: 'Charges & Taxes', icon: '🧾' },
    { id: 'towers', label: 'Towers & Wings', icon: '🏢' },
    { id: 'typologies', label: 'Configurations', icon: '📐' },
    { id: 'inventory', label: 'Inventory Summary', icon: '📦' },
  ];

  return (
    <div style={{ padding: '0' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
        borderRadius: '16px', padding: '28px 32px', marginBottom: '24px',
        color: '#fff', position: 'relative', overflow: 'hidden'
      }}>
        <div style={{ position: 'absolute', top: 0, right: 0, width: '300px', height: '100%', opacity: 0.05,
          background: 'radial-gradient(circle at 70% 50%, #fff 0%, transparent 70%)' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative' }}>
          <div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '4px' }}>
              MASTER DATA
            </div>
            <h1 style={{ fontSize: '28px', fontWeight: '800', margin: '0 0 8px 0' }}>
              {formData.name || 'Project'}
            </h1>
            <div style={{ fontSize: '14px', color: '#cbd5e1', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <span>📍 {formData.location || '-'}, {formData.city || 'Mumbai'}</span>
              <span>🏗️ {formData.number_of_towers || 0} Towers</span>
              <span>🏠 {formData.total_units || 0} Units</span>
              <span style={BADGE(formData.possession_status === 'Ready to Move' ? 'green' : 'amber')}>
                {formData.possession_status || 'Under Construction'}
              </span>
            </div>
          </div>
          <button onClick={handleSave} disabled={saving} style={{
            padding: '12px 28px', borderRadius: '10px', border: 'none', cursor: saving ? 'wait' : 'pointer',
            background: saveStatus === 'success' ? '#10b981' : saveStatus === 'error' ? '#ef4444' : 'linear-gradient(135deg, #3b82f6, #2563eb)',
            color: '#fff', fontWeight: '700', fontSize: '14px', transition: 'all 0.3s',
            boxShadow: '0 4px 12px rgba(59,130,246,0.3)'
          }}>
            {saving ? '⏳ Saving...' : saveStatus === 'success' ? '✅ Saved!' : '💾 Save Master Data'}
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{
        display: 'flex', gap: '4px', marginBottom: '24px', overflowX: 'auto',
        background: '#f1f5f9', borderRadius: '12px', padding: '4px'
      }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveSection(tab.id)} style={{
            padding: '10px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer',
            background: activeSection === tab.id ? '#fff' : 'transparent',
            color: activeSection === tab.id ? '#1e293b' : '#64748b',
            fontWeight: activeSection === tab.id ? '600' : '400',
            fontSize: '13px', whiteSpace: 'nowrap', transition: 'all 0.2s',
            boxShadow: activeSection === tab.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
          }}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* OVERVIEW TAB */}
      {activeSection === 'sitevisits' && <SiteVisits apiBase={apiBase} />}
      {activeSection === 'chart' && <FlatChart apiBase={apiBase} projectId={window._trcProjectId || masterData?.project?.id || 1} />}
      {activeSection === 'overview' && (
        <div>
          {/* Stats Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            {[
              { label: 'Price PSF', value: `₹${(formData.price_per_sqft || formData.base_rate_per_sqft || 0).toLocaleString('en-IN')}`, bg: 'linear-gradient(135deg, #667eea, #764ba2)' },
              { label: 'Total Units', value: formData.total_units || 0, bg: 'linear-gradient(135deg, #f093fb, #f5576c)' },
              { label: 'Available', value: formData.total_available || 0, bg: 'linear-gradient(135deg, #4facfe, #00f2fe)' },
              { label: 'Booked', value: formData.total_booked || 0, bg: 'linear-gradient(135deg, #43e97b, #38f9d7)' },
              { label: 'Towers', value: masterData.project.computed_total_towers || 0, bg: 'linear-gradient(135deg, #fa709a, #fee140)' },
              { label: 'Min Price', value: formatCurrency(formData.min_price), bg: 'linear-gradient(135deg, #a18cd1, #fbc2eb)' },
              { label: 'Max Price', value: formatCurrency(formData.max_price), bg: 'linear-gradient(135deg, #fccb90, #d57eeb)' },
              { label: 'RERA', value: formData.rera_number ? '✅ Registered' : '❌ Missing', bg: 'linear-gradient(135deg, #96fbc4, #f9f586)' },
            ].map((card, i) => (
              <div key={i} style={{ ...STAT_CARD, background: card.bg }}>
                <div style={{ fontSize: '11px', textTransform: 'uppercase', opacity: 0.8, letterSpacing: '1px' }}>{card.label}</div>
                <div style={{ fontSize: '22px', fontWeight: '800', marginTop: '4px' }}>{card.value}</div>
              </div>
            ))}
          </div>

          {/* Cost Sheet Summary */}
          <div style={SECTION_STYLE}>
            <div style={SECTION_TITLE}>🧾 Cost Components (from Cost Sheet Config)</div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ padding: '10px', textAlign: 'left', fontSize: '12px', color: '#64748b' }}>Component</th>
                  <th style={{ padding: '10px', textAlign: 'left', fontSize: '12px', color: '#64748b' }}>Type</th>
                  <th style={{ padding: '10px', textAlign: 'right', fontSize: '12px', color: '#64748b' }}>Rate</th>
                  <th style={{ padding: '10px', textAlign: 'center', fontSize: '12px', color: '#64748b' }}>%?</th>
                  <th style={{ padding: '10px', textAlign: 'center', fontSize: '12px', color: '#64748b' }}>Mandatory</th>
                </tr>
              </thead>
              <tbody>
                {(costSheetItems || []).map((item, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px', fontWeight: '500' }}>{item.component_name}</td>
                    <td style={{ padding: '10px' }}><span style={BADGE('blue')}>{item.component_type}</span></td>
                    <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600' }}>
                      {item.is_percentage ? `${item.rate}%` : formatCurrency(item.rate)}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{item.is_percentage ? '✅' : '—'}</td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{item.is_mandatory ? '✅' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Inventory Status */}
          {statusSummary && Object.keys(statusSummary).length > 0 && (
            <div style={SECTION_STYLE}>
              <div style={SECTION_TITLE}>📦 Inventory Status</div>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                {Object.entries(statusSummary).map(([status, count]) => (
                  <div key={status} style={{
                    padding: '16px 24px', borderRadius: '10px', textAlign: 'center',
                    background: status === 'Open For Sale' ? '#d1fae5' : status === 'Booked' ? '#fce7f3' : status === 'Hold' ? '#fef3c7' : '#f1f5f9',
                    minWidth: '120px'
                  }}>
                    <div style={{ fontSize: '28px', fontWeight: '800' }}>{count}</div>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>{status}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* PROJECT INFO TAB */}
      {activeSection === 'project' && (
        <div style={SECTION_STYLE}>
          <div style={SECTION_TITLE}>🏗️ Project Information</div>
          <div style={GRID2}>
            {[
              { label: 'Project Name', field: 'name', type: 'text' },
              { label: 'Developer Name', field: 'developer_name', type: 'text' },
              { label: 'Location', field: 'location', type: 'text' },
              { label: 'Sub Location', field: 'sub_location', type: 'text' },
              { label: 'Address', field: 'address', type: 'text' },
              { label: 'City', field: 'city', type: 'text' },
              { label: 'State', field: 'state', type: 'text' },
              { label: 'Pincode', field: 'pincode', type: 'text' },
              { label: 'Contact Email', field: 'contact_email', type: 'email' },
              { label: 'Contact Phone', field: 'contact_phone', type: 'text' },
              { label: 'Website URL', field: 'website_url', type: 'url' },
              { label: 'Brochure URL', field: 'brochure_url', type: 'url' },
            ].map(f => (
              <div key={f.field}>
                <div style={LABEL}>{f.label}</div>
                <input type={f.type} style={INPUT} value={formData[f.field] || ''} onChange={e => handleChange(f.field, e.target.value)} />
              </div>
            ))}
            <div>
              <div style={LABEL}>Status</div>
              <select style={SELECT_STYLE} value={formData.status || 'active'} onChange={e => handleChange('status', e.target.value)}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <div>
              <div style={LABEL}>Possession Status</div>
              <select style={SELECT_STYLE} value={formData.possession_status || 'Under Construction'} onChange={e => handleChange('possession_status', e.target.value)}>
                <option value="Under Construction">Under Construction</option>
                <option value="Nearing Possession">Nearing Possession</option>
                <option value="Ready to Move">Ready to Move</option>
              </select>
            </div>
          </div>
          <div style={{ marginTop: '16px' }}>
            <div style={LABEL}>Description</div>
            <textarea style={{ ...INPUT, minHeight: '80px', resize: 'vertical' }} value={formData.description || ''} onChange={e => handleChange('description', e.target.value)} />
          </div>
        </div>
      )}

      {/* RERA TAB */}
      {activeSection === 'rera' && (
        <div style={SECTION_STYLE}>
          <div style={SECTION_TITLE}>📋 RERA & Legal</div>
          <div style={GRID2}>
            <div>
              <div style={LABEL}>RERA Number</div>
              <input style={INPUT} value={formData.rera_number || ''} onChange={e => handleChange('rera_number', e.target.value)} />
            </div>
            <div>
              <div style={LABEL}>RERA Date</div>
              <input type="date" style={INPUT} value={formData.rera_date || ''} onChange={e => handleChange('rera_date', e.target.value)} />
            </div>
            <div>
              <div style={LABEL}>MahaRERA URL</div>
              <input type="url" style={INPUT} value={formData.maharera_url || ''} onChange={e => handleChange('maharera_url', e.target.value)} />
            </div>
            <div>
              <div style={LABEL}>RERA Certificate URL</div>
              <input type="url" style={INPUT} value={formData.rera_certificate_url || ''} onChange={e => handleChange('rera_certificate_url', e.target.value)} />
            </div>
            <div>
              <div style={LABEL}>Proposed Possession Date</div>
              <input type="date" style={INPUT} value={formData.proposed_possession_date || ''} onChange={e => handleChange('proposed_possession_date', e.target.value)} />
            </div>
            <div>
              <div style={LABEL}>Legal Charges (₹)</div>
              <input type="number" style={INPUT} value={formData.legal_charges || 0} onChange={e => handleChange('legal_charges', parseFloat(e.target.value) || 0)} />
            </div>
          </div>
          {formData.rera_number && (
            <div style={{ marginTop: '16px', padding: '12px 16px', background: '#d1fae5', borderRadius: '8px', fontSize: '13px', color: '#065f46' }}>
              ✅ Project is RERA Registered: <strong>{formData.rera_number}</strong>
            </div>
          )}
        </div>
      )}

      {/* PRICING TAB */}
      {activeSection === 'pricing' && (
        <div style={SECTION_STYLE}>
          <div style={SECTION_TITLE}>💰 Pricing</div>
          <div style={GRID3}>
            <div>
              <div style={LABEL}>Base Price per Sq.Ft (₹)</div>
              <input type="number" style={INPUT} value={formData.price_per_sqft || formData.base_rate_per_sqft || 0}
                onChange={e => { const v = parseFloat(e.target.value) || 0; handleChange('price_per_sqft', v); handleChange('base_rate_per_sqft', v); }} />
            </div>
            <div>
              <div style={LABEL}>Floor Rise per Floor (₹)</div>
              <input type="number" style={INPUT} value={formData.floor_rise_rate || 0} onChange={e => handleChange('floor_rise_rate', parseFloat(e.target.value) || 0)} />
            </div>
            <div>
              <div style={LABEL}>Total Towers</div>
              <input type="number" style={INPUT} value={formData.number_of_towers || 0} onChange={e => handleChange('number_of_towers', parseInt(e.target.value) || 0)} />
            </div>
            <div>
              <div style={LABEL}>Total Levels / Floors</div>
              <input type="number" style={INPUT} value={formData.levels || 0} onChange={e => handleChange('levels', parseInt(e.target.value) || 0)} />
            </div>
            <div>
              <div style={LABEL}>Total Units</div>
              <input type="number" style={INPUT} value={formData.total_units || 0} onChange={e => handleChange('total_units', parseInt(e.target.value) || 0)} />
            </div>
            <div>
              <div style={LABEL}>Total Flats</div>
              <input type="number" style={INPUT} value={formData.total_flats || 0} onChange={e => handleChange('total_flats', parseInt(e.target.value) || 0)} />
            </div>
            <div>
              <div style={LABEL}>Min Price (₹)</div>
              <input type="number" style={INPUT} value={formData.min_price || 0} onChange={e => handleChange('min_price', parseFloat(e.target.value) || 0)} />
            </div>
            <div>
              <div style={LABEL}>Max Price (₹)</div>
              <input type="number" style={INPUT} value={formData.max_price || 0} onChange={e => handleChange('max_price', parseFloat(e.target.value) || 0)} />
            </div>
            <div>
              <div style={LABEL}>Carpet Area Range</div>
              <input style={INPUT} value={formData.carpet_area_range || ''} onChange={e => handleChange('carpet_area_range', e.target.value)} placeholder="e.g. 467-830 sq.ft" />
            </div>
          </div>
          <div style={{ marginTop: '20px', padding: '16px', background: '#eff6ff', borderRadius: '10px', border: '1px solid #bfdbfe' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e40af', marginBottom: '4px' }}>💡 Price Sync</div>
            <div style={{ fontSize: '12px', color: '#3b82f6' }}>
              Changing "Base Price per Sq.Ft" and "Floor Rise" here will automatically sync to Cost Sheet Config for all downstream calculations.
            </div>
          </div>
        </div>
      )}

      {/* CHARGES & TAXES TAB */}
      {activeSection === 'charges' && (
        <div style={SECTION_STYLE}>
          <div style={SECTION_TITLE}>🧾 Charges & Taxes</div>
          <div style={GRID3}>
            <div>
              <div style={LABEL}>Stamp Duty (%)</div>
              <input type="number" step="0.1" style={INPUT} value={formData.stamp_duty_rate || 0} onChange={e => handleChange('stamp_duty_rate', parseFloat(e.target.value) || 0)} />
            </div>
            <div>
              <div style={LABEL}>GST (%)</div>
              <input type="number" step="0.1" style={INPUT} value={formData.gst_rate || 0} onChange={e => handleChange('gst_rate', parseFloat(e.target.value) || 0)} />
            </div>
            <div>
              <div style={LABEL}>Registration Rate (%)</div>
              <input type="number" step="0.1" style={INPUT} value={formData.registration_charge_rate || 0} onChange={e => handleChange('registration_charge_rate', parseFloat(e.target.value) || 0)} />
            </div>
            <div>
              <div style={LABEL}>Registration Charges (₹ flat)</div>
              <input type="number" style={INPUT} value={formData.registration_flat_amount || 0} onChange={e => handleChange('registration_flat_amount', parseFloat(e.target.value) || 0)} />
            </div>
            <div>
              <div style={LABEL}>Legal Charges (₹)</div>
              <input type="number" style={INPUT} value={formData.legal_charges || 0} onChange={e => handleChange('legal_charges', parseFloat(e.target.value) || 0)} />
            </div>
            <div>
              <div style={LABEL}>Car Parking (₹)</div>
              <input type="number" style={INPUT} value={formData.car_parking_charge || 0} onChange={e => handleChange('car_parking_charge', parseFloat(e.target.value) || 0)} />
            </div>
            <div>
              <div style={LABEL}>Maintenance Deposit (₹/sqft)</div>
              <input type="number" style={INPUT} value={formData.maintenance_deposit || 0} onChange={e => handleChange('maintenance_deposit', parseFloat(e.target.value) || 0)} />
            </div>
            <div>
              <div style={LABEL}>Corpus Fund (₹)</div>
              <input type="number" style={INPUT} value={formData.corpus_fund || 0} onChange={e => handleChange('corpus_fund', parseFloat(e.target.value) || 0)} />
            </div>
            <div>
              <div style={LABEL}>Infra Charges (₹)</div>
              <input type="number" style={INPUT} value={formData.infra_charges || 0} onChange={e => handleChange('infra_charges', parseFloat(e.target.value) || 0)} />
            </div>
            <div>
              <div style={LABEL}>Advance Maintenance (₹)</div>
              <input type="number" style={INPUT} value={formData.advance_maintenance || 0} onChange={e => handleChange('advance_maintenance', parseFloat(e.target.value) || 0)} />
            </div>
            <div>
              <div style={LABEL}>{formData.other_charges_label || 'Other Charges'} (₹)</div>
              <input type="number" style={INPUT} value={formData.other_charges || 0} onChange={e => handleChange('other_charges', parseFloat(e.target.value) || 0)} />
            </div>
            <div>
              <div style={LABEL}>Other Charges Label</div>
              <input style={INPUT} value={formData.other_charges_label || 'Other Charges'} onChange={e => handleChange('other_charges_label', e.target.value)} />
            </div>
          </div>
          <div style={{ marginTop: '20px', padding: '16px', background: '#fef3c7', borderRadius: '10px', border: '1px solid #fcd34d' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#92400e', marginBottom: '4px' }}>⚠️ Sync Notice</div>
            <div style={{ fontSize: '12px', color: '#b45309' }}>
              Saving these values will sync Stamp Duty, GST, Registration, Legal Charges, Floor Rise, and Car Parking to the Cost Sheet Config used by the Cost Sheet and NPV Calculator.
            </div>
          </div>
        </div>
      )}

      {/* TOWERS TAB */}
      {activeSection === 'towers' && (
        <div style={SECTION_STYLE}>
          <div style={SECTION_TITLE}>🏢 Towers & Wings ({towers.length} total)</div>
          {towers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>No towers configured. Add towers in the Projects page.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    {['Tower/Wing', 'Building', 'Floors', 'Units/Floor', 'Total Units', 'Inventory', 'BHK Types', 'Status'].map(h => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#475569', fontSize: '11px', textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {towers.map((t, i) => (
                    <tr key={t.id} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ padding: '10px 12px', fontWeight: '600' }}>{t.tower_name}</td>
                      <td style={{ padding: '10px 12px' }}>{t.building || '-'}</td>
                      <td style={{ padding: '10px 12px' }}>{t.number_of_floors || t.total_floors || '-'}</td>
                      <td style={{ padding: '10px 12px' }}>{t.units_per_floor || '-'}</td>
                      <td style={{ padding: '10px 12px', fontWeight: '600' }}>{t.total_units || '-'}</td>
                      <td style={{ padding: '10px 12px' }}><span style={BADGE('blue')}>{t.unit_count || 0} units</span></td>
                      <td style={{ padding: '10px 12px', fontSize: '12px' }}>{t.bhk_types || '-'}</td>
                      <td style={{ padding: '10px 12px' }}><span style={BADGE(t.status === 'active' ? 'green' : 'gray')}>{t.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* TYPOLOGIES TAB */}
      {activeSection === 'typologies' && (
        <div style={SECTION_STYLE}>
          <div style={SECTION_TITLE}>📐 Configurations / Typologies ({typologies.length} types)</div>
          {typologies.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>No typologies configured.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    {['BHK Type', 'Carpet Area (sq.ft)', 'Super Area (sq.ft)', 'Price PSF (₹)', 'Base Price (₹)'].map(h => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#475569', fontSize: '11px', textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {typologies.map((t, i) => (
                    <tr key={t.id} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ padding: '10px 12px', fontWeight: '600' }}>{t.bhk_type}</td>
                      <td style={{ padding: '10px 12px' }}>{t.carpet_area ? `${t.carpet_area} sq.ft` : '-'}</td>
                      <td style={{ padding: '10px 12px' }}>{t.super_area ? `${t.super_area} sq.ft` : '-'}</td>
                      <td style={{ padding: '10px 12px' }}>{t.price_per_sqft ? `₹${t.price_per_sqft.toLocaleString('en-IN')}` : '-'}</td>
                      <td style={{ padding: '10px 12px', fontWeight: '600' }}>{formatCurrency(t.base_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* INVENTORY SUMMARY TAB */}
      {activeSection === 'inventory' && (
        <div style={SECTION_STYLE}>
          <div style={SECTION_TITLE}>📦 Inventory Summary by Wing & BHK</div>
          {inventorySummary.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>No inventory data available.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    {['Wing', 'BHK', 'Status', 'Count', 'Avg Area', 'Min Area', 'Max Area'].map(h => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#475569', fontSize: '11px', textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {inventorySummary.map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ padding: '10px 12px', fontWeight: '600' }}>{r.wing}</td>
                      <td style={{ padding: '10px 12px' }}>{r.bhk}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={BADGE(r.sale_status === 'Open For Sale' ? 'green' : r.sale_status === 'Booked' ? 'amber' : 'gray')}>
                          {r.sale_status}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', fontWeight: '600' }}>{r.count}</td>
                      <td style={{ padding: '10px 12px' }}>{r.avg_carpet_area || '-'} sq.ft</td>
                      <td style={{ padding: '10px 12px' }}>{r.min_carpet_area || '-'} sq.ft</td>
                      <td style={{ padding: '10px 12px' }}>{r.max_carpet_area || '-'} sq.ft</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MasterData;
